import {
  DestroyRef,
  Directive,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store } from "@ngxs/store";
import { UserResponse } from "@shared/interfaces/user.model";

@Directive({
  selector: "[appHasRole]",
  standalone: true,
})
export class HasRoleDirective implements OnInit {
  @Input() public appHasRole: string;
  @Input() public appHasRoleNoAccessSection: boolean;

  public isVisible = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly viewContainerRef: ViewContainerRef,
    private readonly templateRef: TemplateRef<any>,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.store
      .select<UserResponse>(
        (state) => state?.UserState?.user || state?.SatUserState?.user,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (!user) {
          this.viewContainerRef.clear();
          return;
        }

        const roles = this.appHasRole ? this.appHasRole.split(",") : [];
        const fullList = this.flattenMenus(user);
        const hasPermission = roles.every((item) => fullList.includes(item));

        const shouldShow = this.appHasRoleNoAccessSection
          ? !hasPermission
          : hasPermission;
        this.setVisibility(shouldShow);
      });
  }

  private setVisibility(shouldShow: boolean): void {
    if (this.isVisible === shouldShow) {
      return;
    }

    this.viewContainerRef.clear();
    if (shouldShow) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
    }
    this.isVisible = shouldShow;
  }

  public flattenMenus(user: UserResponse): string[] {
    const menuSubUserAccess = user.menuSubUserAccess
      ? user.menuSubUserAccess
          .map((item) => item.menuId)
          .concat(user.menuSubUserAccess.map((item) => item.menuIdSub))
      : [];

    const menuList = user.menuList
      ? user.menuList
          .map((item) => item.menuId)
          .concat(user.menuList.map((item) => item.menuId))
      : [];

    const menuAccess = user.menuAccess || [];
    const fullList = ([] as string[]).concat(
      menuList,
      menuSubUserAccess,
      menuAccess,
    );

    return Array.from(new Set(fullList));
  }
}
